import { useApiResource } from '@texas/api/hooks/useApiResource';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { useEffect } from 'react';
import { reactEvents } from '@bridge/reactEvents';
import { SpinnerWithLabel } from '../../shared/SpinnerWithLabel';
import { ErrorDetails } from '../../shared/alert/ErrorDetails';
import { BodyOverride } from '../../shared/BodyOverride';
import { SupplierBranchInfo } from './SupplierBranchInfo';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { Role } from '@texas/api/endpoints/userApi';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from '@chakra-ui/react';
import { EntityTagLinks } from '@texas/components/tags/EntityTagLinks';
import { EntityType } from '@texas/api/endpoints/trackerApi';

interface Props {
  supplierId: number;
}

export function SupplierManagement({ supplierId }: Props) {
  const {
    data: supplier,
    refetch,
    loading,
    error,
  } = useApiResource(suppliersApi.get);

  const { t } = useTranslation();

  useEffect(() => {
    refetch(supplierId);
  }, [refetch, supplierId]);

  useEffect(() => {
    return reactEvents.supplierBranchUpdated.subscribe((updatedSupplierId) => {
      if (updatedSupplierId === supplierId) {
        refetch(supplierId);
      }
    });
  }, [supplierId, refetch]);

  return (
    <BodyOverride>
      <RoleRestrict
        allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
      >
        {loading && <SpinnerWithLabel label={t('general.loadingData')} />}
        <ErrorDetails error={error} />
        {supplier && (
          <Flex direction="column" gap={6}>
            <Box
              bg="entityDirectory.surface"
              border="1px solid"
              borderColor="entityDirectory.border"
              p={6}
            >
              <EntityTagLinks
                entityId={supplier.id}
                entityType={EntityType.Supplier}
              />
            </Box>
            <SupplierBranchInfo
              branchData={supplier.branchData}
              supplierId={supplier.id}
            />
          </Flex>
        )}
      </RoleRestrict>
    </BodyOverride>
  );
}
